import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { trackConversion } from "@/lib/pixels";
import { getAttribution } from "@/lib/attribution";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const isDSP = (s) => typeof s === "string" && s.includes("Disability Support Pension");

export default function ContactForm({ source = "general", defaultService = "" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: defaultService, message: "" });
  const [status, setStatus] = useState({ loading: false, success: false, error: "" });
  const navigate = useNavigate();

  const onChange = (e) => {
    const { name, value } = e.target;
    // GA4 + dataLayer: user selected the DSP service in the booking form
    if (name === "service" && isDSP(value)) {
      try { trackConversion("dsp_booking_selection", { service_type: "DSP" }); } catch (_) {}
    }
    setForm({ ...form, [name]: value });
  };

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, success: false, error: "" });
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, source_page: source, attribution: getAttribution() }),
      });
      if (!res.ok) throw new Error("Request failed");
      const data = await res.json();
      // Stash ONLY the non-identifying backend submission id so /thank-you can
      // fire the single Google Ads conversion once and dedupe against refresh /
      // back-forward. No personal data (name/email/phone) is stored anymore —
      // Enhanced Conversions have been removed (Task 7).
      try {
        sessionStorage.setItem("__fca_ec", JSON.stringify({
          id: data?.id || "",
          ts: Date.now(),
        }));
      } catch (_) { /* private mode / storage disabled — proceed without dedupe */ }
      // GA4 + dataLayer: successful enquiry that originated from the DSP page
      // or selected the DSP service (does NOT affect the primary Ads conversion).
      let dspOrigin = false;
      try { dspOrigin = sessionStorage.getItem("__fca_dsp_origin") === "1"; } catch (_) {}
      if (isDSP(form.service) || dspOrigin) {
        try { trackConversion("dsp_enquiry_submit", { service_type: "DSP" }); } catch (_) {}
        try { sessionStorage.removeItem("__fca_dsp_origin"); } catch (_) {}
      }
      // Navigate to dedicated /thank-you page so Google Ads conversion fires + URL changes
      // for cleaner Goal/Conversion attribution in GA4 + Google Ads reporting.
      navigate(`/thank-you?source=${encodeURIComponent(source)}&type=enquiry`);
    } catch (err) {
      setStatus({ loading: false, success: false, error: "Could not submit. Please try again or call us directly." });
    }
  };

  if (status.success) {
    return null;
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1.5">Name *</label>
          <input required name="name" value={form.name} onChange={onChange} className="w-full rounded-lg border-stone-300 border px-3.5 py-2.5 focus:border-teal-600 focus:ring-1 focus:ring-teal-600 outline-none" data-testid="contact-name" />
        </div>
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1.5">Email *</label>
          <input required type="email" name="email" value={form.email} onChange={onChange} className="w-full rounded-lg border-stone-300 border px-3.5 py-2.5 focus:border-teal-600 focus:ring-1 focus:ring-teal-600 outline-none" data-testid="contact-email" />
        </div>
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1.5">Phone</label>
          <input name="phone" value={form.phone} onChange={onChange} className="w-full rounded-lg border-stone-300 border px-3.5 py-2.5 focus:border-teal-600 focus:ring-1 focus:ring-teal-600 outline-none" data-testid="contact-phone" />
        </div>
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1.5">Service of interest</label>
          <select name="service" value={form.service} onChange={onChange} className="w-full rounded-lg border-stone-300 border px-3.5 py-2.5 focus:border-teal-600 focus:ring-1 focus:ring-teal-600 outline-none bg-white" data-testid="contact-service">
            <option value="">— Select —</option>
            <option>Functional Capacity Assessment</option>
            <option>Short Term Accommodation Assessment</option>
            <option>Individualised Living Options Assessment</option>
            <option>Supported Independent Living Assessment</option>
            <option>Psychosocial Functional Assessment</option>
            <option>Assistive Technology Assessment</option>
            <option>Disability Support Pension (DSP) Assessment</option>
            <option>Other</option>
          </select>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-stone-700 mb-1.5">Message</label>
        <textarea name="message" rows={4} value={form.message} onChange={onChange} className="w-full rounded-lg border-stone-300 border px-3.5 py-2.5 focus:border-teal-600 focus:ring-1 focus:ring-teal-600 outline-none" data-testid="contact-message" />
      </div>
      {status.error && <div className="text-red-600 text-sm" data-testid="contact-error">{status.error}</div>}
      <button type="submit" disabled={status.loading} className="bg-teal-700 hover:bg-teal-800 disabled:opacity-60 text-white px-7 py-3 rounded-full font-semibold transition" data-testid="contact-submit">
        {status.loading ? "Submitting..." : "Submit Enquiry"}
      </button>
    </form>
  );
}
